// Layer 4 (UI) — HSE & regulatory compliance (locked comp
// `.impeccable/mocks/decision/compliance.html`, variant B · safety record
// first, picked 25 Sep with A's day-bar permit register as the default tab and
// C's monthly bars as the Safety trend tab). Paul's HSE dashboard is the page:
// the LTI-free header fact, one four-cell band, the safety table beside the
// Superintendent's Due next, then four drill-down tabs. Every figure reads
// Scene.transform.ts; this file composes and lays out.

import { useEffect, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  GlassCard,
  HelpAnchor,
  Pill,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TITLE_LARGE } from '@/shared/ui-class';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { Commentable } from '@/shared/comments/Commentable';
import type { RoleTable } from '@/versions/v1/schema/plant-role';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { ActorChip } from '../advisory/PipelineStrip';
import { CARD_TABLE, DowntimeCard, SYN_CHIP } from '../downtime/DowntimeCard';
import {
  COMPLIANCE_TABS,
  type ComplianceTab,
  type CrewKpiCell,
  type CrewTrend,
  type CrewGap,
  type HseBandCell,
  type HseDueNext,
  type HseDueNextRow,
  type HseHeaderFact,
  type HseRecordableMonth,
  type HseTextRun,
  type PermitDayRegister,
  type RegulatoryRegister,
  type RollingFact,
  type SafetyRecord,
  complianceTabFromSearchParam,
  complianceTabHeadlines,
  complianceCopy,
  crewComplianceCellTone,
  crewGaps,
  crewKpiCells,
  crewMatrixCaption,
  crewTableWithRollingLabel,
  crewTrend,
  hseBand,
  hseDashboardView,
  hseDueNext,
  hseHeaderFact,
  permitDayRegister,
  recordableMonths,
  recordableStripLabel,
  regulatoryRegister,
  rollingTwelveFacts,
  safetyRecord,
  tablesForComplianceTab,
} from './Scene.transform';

const HELP_DOC: Record<ComplianceTab, string> = {
  permits: 'compliance/hse',
  compliance: 'compliance/hse',
  crew: 'compliance/crew',
  trend: 'compliance/hse',
};

const PILL_SM = 'whitespace-nowrap px-2 py-px text-[10px] font-semibold tracking-[0.05em]';
/** Caps label for in-card captions and strip heads (DESIGN § Type ramp). */
const CAPS = 'text-[10px] font-semibold uppercase tracking-[0.07em] text-muted';
/** A flush card: its table runs edge to edge under a hairline. */
const FLUSH_CARD = 'px-0 pb-0 pt-3 [&>header]:px-4';
const FLUSH_TABLE = cn(
  CARD_TABLE,
  'border-t border-[var(--hairline)] [&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4',
);
const SOFT_BAR = 'color-mix(in srgb, var(--ink-secondary) 18%, transparent)';
const WARN_SOFT = 'color-mix(in srgb, var(--state-warning) 12%, transparent)';

export function ComplianceScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = projectTileBySlug(projectId);
  const plantName = project?.name ?? 'this plant';
  const copy = complianceCopy(plantName);
  const { state } = useScenario();
  const [tab, setTab] = useState<ComplianceTab>('permits');

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const requestedTab = complianceTabFromSearchParam(tabParam);
    if (requestedTab) setTab(requestedTab);
  }, [searchParams]);

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  const selectTab = (next: ComplianceTab) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const headlines = complianceTabHeadlines(state);
  const tabItems = COMPLIANCE_TABS.map((item) => ({ ...item, count: headlines[item.id] }));

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ComplianceScene">
        <SurfacePageBody className="min-h-[calc(100vh-3.2rem)] space-y-3" width="full">
          <HseHeader title={copy.title} fact={hseHeaderFact(state)} helpDoc={HELP_DOC[tab]} />
          <HseBand cells={hseBand(state)} />
          <Commentable name="HSEDashboard">
            <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-stretch">
              <SafetyPerformance state={state} className="xl:col-span-8" />
              <DueNext read={hseDueNext(state)} className="xl:col-span-4" />
            </div>
          </Commentable>
          <SurfacePageTabs aria-label="Compliance" items={tabItems} value={tab} onChange={selectTab} variant="ink" className="pt-3" />
          {tab === 'permits' ? <Commentable name="PermitsToWork"><div><PermitRegister read={permitDayRegister(state)} /></div></Commentable> : null}
          {tab === 'compliance' ? <Commentable name="RegulatoryCompliance"><div><RegulatoryObligations read={regulatoryRegister(state)} /></div></Commentable> : null}
          {tab === 'crew' ? <Commentable name="CrewCompliance"><div><CrewComplianceTab /></div></Commentable> : null}
          {tab === 'trend' ? <Commentable name="SafetyTrend"><div><SafetyTrendTab state={state} /></div></Commentable> : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** Comparison runs: the lead term 600 ink-secondary, the one exception in its
 *  state tone. A line only breaks at its ` · ` separators, never inside a term. */
function Runs({ runs }: { runs: readonly HseTextRun[] }) {
  return (
    <>
      {runs.map((run, index) => (
        <span
          key={`${index}-${run.text}`}
          className={cn(
            run.strong ? 'font-semibold text-ink-secondary' : undefined,
            run.tone === 'warn' ? '!text-[var(--state-warning)]' : undefined,
          )}
        >
          {run.text.split(/(?<= · )/).map((part, partIndex) => (
            <span key={partIndex} className="whitespace-pre">
              {part}
              {part.endsWith(' · ') ? <wbr /> : null}
            </span>
          ))}
        </span>
      ))}
    </>
  );
}

function HumanChip({ name }: { name: string }) {
  return <ActorChip actor="Human">{name}</ActorChip>;
}

// --- Header and band ----------------------------------------------------------

/** One header row on the canvas: the title left, the LTI-free fact right with
 *  its comparison under it, then the page's one [syn] chip and help. */
function HseHeader({ title, fact, helpDoc }: { title: string; fact: HseHeaderFact; helpDoc: string }) {
  return (
    <header className="flex flex-wrap items-end gap-x-6 gap-y-3">
      <h1 className={cn(SURFACE_PAGE_TITLE_LARGE, 'mr-auto min-w-0')}>{title}</h1>
      <div className="flex items-center gap-3">
        <div data-hse-header-fact className="flex flex-col items-end tabular-nums">
          <span className={CAPS}>{fact.label}</span>
          <span className="text-2xl font-semibold leading-tight tracking-[-0.01em] text-ink-primary">
            <span data-hse-header-days>{fact.days}</span>
            <span className="ml-1 text-sm font-semibold tracking-normal text-muted">{fact.unit}</span>
          </span>
          <span className="whitespace-nowrap text-xs text-muted">
            <Runs runs={fact.comparison} />
          </span>
        </div>
        {SYN_CHIP}
        <HelpAnchor doc={helpDoc} label="Compliance" />
      </div>
    </header>
  );
}

/** The flat band: four unordered reads in one hairline-split card, each figure
 *  over its comparison. Only an exception carries a state rule. */
function HseBand({ cells }: { cells: readonly HseBandCell[] }) {
  return (
    <GlassCard
      as="section"
      tone="matte"
      aria-label="HSE status"
      data-hse-band
      className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
    >
      <div className="grid grid-cols-2 xl:grid-cols-4">
        {cells.map((cell) => (
          <div
            key={cell.id}
            data-hse-band-cell={cell.id}
            data-tone={cell.tone ?? 'none'}
            className="relative flex min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] px-4 pb-3 pt-3 last:border-r-0 xl:border-b-0"
          >
            {cell.tone ? <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-[var(--state-warning)]" /> : null}
            <span className="truncate text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.name}</span>
            <span data-hse-band-figure={cell.id} className="text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
              {cell.figure}
              {cell.of ? <span className="ml-1 text-sm font-semibold tracking-normal text-muted">{cell.of}</span> : null}
            </span>
            <span data-hse-band-comparison={cell.id} className="text-xs leading-snug tabular-nums text-muted">
              <Runs runs={cell.comparison} />
            </span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// --- The 8/4 row --------------------------------------------------------------

function SafetyPerformance({ state, className }: { state: SessionState; className?: string }) {
  const view = hseDashboardView(state);
  return (
    <DowntimeCard title="Safety performance" meta={view.safety.periodMeta} className={cn('flex flex-col', className)}>
      <div className={cn(CARD_TABLE, '[&_td]:py-2.5')}>
        <Table wrap={false} className="tabular-nums" data-hse-safety-table>
          <THead>
            <TR>
              <TH>Metric</TH>
              <TH className="text-right">Year 1</TH>
              <TH className="text-right">Prior year</TH>
              <TH className="text-right">Target</TH>
            </TR>
          </THead>
          <TBody>
            {view.safety.metrics.map((metric) => (
              <TR key={metric.id} data-hse-metric={metric.id}>
                <TD className="font-medium !text-ink-primary">{metric.label}</TD>
                <TD className="text-right font-semibold !text-ink-primary">{metric.yearToDate}</TD>
                <TD className="text-right !text-muted">{metric.priorOperatingYear}</TD>
                <TD className="whitespace-nowrap text-right">{metric.target}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      <RecordableStrip months={recordableMonths(state)} range={recordableStripLabel(state)} />
    </DowntimeCard>
  );
}

/** Recordable injuries by month under the table: a hairline ring for a clean
 *  month, an ink ring for an MTI or RWC, a filled blocked dot for an LTI. */
function RecordableStrip({ months, range }: { months: readonly HseRecordableMonth[]; range: string }) {
  return (
    <div data-recordable-strip className="mt-3.5 border-t border-[var(--hairline)] pt-3">
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className={CAPS}>Recordable injuries by month · {range}</span>
        <span className="text-xs text-muted">
          <span className="font-semibold text-ink-secondary">ring</span> MTI or RWC ·{' '}
          <span className="font-semibold text-[var(--state-blocked)]">filled</span> LTI · dashed = Year 1 starts
        </span>
      </div>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${months.length}, minmax(0, 1fr))` }}>
        {months.map((month) => (
          <span key={month.id} data-recordable-month={month.id} data-kind={month.kind} className="relative flex flex-col items-center gap-[3px]">
            {month.yearStart ? <span aria-hidden className="absolute -bottom-0.5 -left-0.5 -top-1 border-l border-dashed border-[var(--ink-tertiary)]" /> : null}
            <span
              aria-hidden
              className={cn(
                'block h-[11px] w-[11px] rounded-full',
                month.kind === 'lti'
                  ? 'border border-[var(--state-blocked)] bg-[var(--state-blocked)]'
                  : month.kind === 'recordable'
                    ? 'border-[1.5px] border-[var(--ink-secondary)]'
                    : 'border border-[var(--hairline-strong)]',
              )}
            />
            <span className="whitespace-nowrap text-[10px] text-muted">{month.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function DueCaption({ label, count }: { label: string; count: number }) {
  return (
    <div className={cn(CAPS, 'flex justify-between border-b border-[var(--hairline)] pb-1 pt-1 [&+*]:pt-1.5')}>
      <span>{label}</span>
      <span className="text-ink-secondary">{count}</span>
    </div>
  );
}

function DueRow({ row, oneLine }: { row: HseDueNextRow; oneLine?: boolean }) {
  return (
    <div
      data-due-next={row.id}
      className="grid grid-cols-[46px_minmax(0,1fr)_auto] items-baseline gap-x-3 border-b border-[var(--hairline)] py-1.5 last:border-b-0"
    >
      <span
        className={cn(
          'whitespace-nowrap text-xs tabular-nums',
          oneLine ? 'font-medium text-ink-secondary' : 'font-semibold text-ink-primary',
          row.warn ? '!text-[var(--state-warning)]' : undefined,
        )}
      >
        {row.when}
      </span>
      {/* At 1470 the comp draws each next-30-day title on one truncated line;
          below 1400 the column is 92–119 px, so the title wraps freely like the
          Today rows above rather than being cut by a clamp. */}
      <span
        data-due-next-title={row.id}
        className={cn('min-w-0 text-[12.5px] leading-snug text-ink-primary', oneLine ? 'font-medium min-[1400px]:truncate' : 'font-semibold')}
      >
        {row.what}
        {!oneLine && row.detail ? <span className="block text-xs font-normal text-muted">{row.detail}</span> : null}
      </span>
      <span className="self-center">
        <HumanChip name={row.owner} />
      </span>
    </div>
  );
}

/** The Superintendent's action: today before the shift ends, then the next 30
 *  days; each row a time, an action and its named owner. */
function DueNext({ read, className }: { read: HseDueNext; className?: string }) {
  return (
    <DowntimeCard title="Due next" meta="time · action · owner" className={className} aria-label="Due next">
      <div data-due-next-today>
        <DueCaption label={read.todayLabel} count={read.today.length} />
        {read.today.map((row) => <DueRow key={row.id} row={row} />)}
      </div>
      {read.later.length > 0 ? (
        <div data-due-next-later className="mt-1.5">
          <DueCaption label={read.laterLabel} count={read.later.length} />
          {read.later.map((row) => <DueRow key={row.id} row={row} oneLine />)}
        </div>
      ) : null}
    </DowntimeCard>
  );
}

// --- Permits to work (default tab) -------------------------------------------

function PermitStatus({ status }: { status: PermitDayRegister['rows'][number]['status'] }) {
  if (status.kind === 'under-2h') return <Pill tone="warning" className={PILL_SM}>{status.label}</Pill>;
  if (status.kind === 'expired') return <Pill tone="danger" className={PILL_SM}>{status.label}</Pill>;
  if (status.kind === 'active') {
    return (
      <span className="inline-flex items-center whitespace-nowrap text-xs font-semibold text-ink-secondary">
        <span aria-hidden className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[var(--ink-tertiary)]" />
        {status.label}
      </span>
    );
  }
  return <span className="whitespace-nowrap text-xs font-medium text-muted">{status.label}</span>;
}

function DayBar({ bar, cursorLeft }: { bar: PermitDayRegister['rows'][number]['bar']; cursorLeft: number }) {
  return (
    <div className="relative h-3 rounded border border-[var(--hairline)] bg-[var(--surface-1)]">
      <i
        className="absolute bottom-[2px] top-[2px] rounded-sm"
        style={{ left: `${bar.worked.left}%`, width: `${bar.worked.width}%`, background: bar.closed ? SOFT_BAR : 'var(--ink-secondary)' }}
      />
      {bar.ahead ? (
        <i
          className={cn('absolute bottom-px top-px rounded-sm border border-dashed', bar.warn ? 'border-[var(--state-warning)]' : 'border-[var(--ink-tertiary)]')}
          style={{ left: `${bar.ahead.left}%`, width: `${bar.ahead.width}%`, background: bar.warn ? WARN_SOFT : undefined }}
        />
      ) : null}
      <span aria-hidden className="absolute -bottom-[5px] -top-[5px] border-l-[1.5px] border-[var(--ink-primary)]" style={{ left: `${cursorLeft}%` }} />
    </div>
  );
}

/** Today's permits drawn against the day shift: solid for time worked to the
 *  cursor, dashed to expiry, warning under two hours, closed permits soft. */
function PermitRegister({ read }: { read: PermitDayRegister }) {
  return (
    <DowntimeCard
      title="Permit register"
      meta={
        <>
          today · <span className="font-semibold text-ink-secondary">solid</span> worked to {read.cursorClock} ·{' '}
          <span className="font-semibold text-ink-secondary">dashed</span> permitted to expiry
        </>
      }
      className={FLUSH_CARD}
      aria-label="Permit register"
    >
      <div className={FLUSH_TABLE}>
        <Table wrap={false} className="tabular-nums" data-permit-register>
          <THead>
            <TR>
              <TH>Permit</TH>
              <TH>Location / work</TH>
              <TH>Crew</TH>
              <TH className="w-[30%] min-w-[200px] !normal-case !tracking-normal">
                <div className="relative h-[13px] text-[10px] font-medium text-muted">
                  {read.ticks.map((tick) => (
                    <span key={tick.label} className="absolute top-0 -translate-x-1/2" style={{ left: `${tick.left}%` }}>
                      {tick.label}
                    </span>
                  ))}
                </div>
              </TH>
              <TH className="text-right">Expires</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {read.rows.map((row) => {
              const closed = row.status.kind === 'closed';
              return (
                <TR key={row.id} data-permit-row={row.id} data-permit-status={row.status.kind}>
                  <TD className="whitespace-nowrap">
                    <span className={cn('block text-xs tracking-[0.04em]', closed ? 'text-ink-secondary' : 'text-ink-primary')}>{row.id}</span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">{row.type}</span>
                  </TD>
                  <TD className="min-w-[190px]">
                    <span className={cn('block text-[12.5px] font-semibold leading-snug', closed ? 'text-ink-secondary' : 'text-ink-primary')}>{row.work}</span>
                    <span className="block text-xs text-muted">{row.area} · issued {row.issued}</span>
                  </TD>
                  <TD>
                    <span className="flex flex-wrap gap-1 min-[1400px]:flex-nowrap">
                      {row.crew.map((name) => <HumanChip key={name} name={name} />)}
                    </span>
                  </TD>
                  <TD>
                    <DayBar bar={row.bar} cursorLeft={read.cursorLeft} />
                  </TD>
                  <TD className="whitespace-nowrap text-right">
                    <span className={cn('block text-xs', closed ? 'font-medium text-ink-secondary' : 'font-semibold text-ink-primary', row.warn ? '!text-[var(--state-warning)]' : undefined)}>
                      {row.expires}
                    </span>
                    {row.left ? <span className="block text-[11px] text-muted">{row.left}</span> : null}
                  </TD>
                  <TD>
                    <PermitStatus status={row.status} />
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>
    </DowntimeCard>
  );
}

// --- Regulatory compliance ----------------------------------------------------

function ObligationStatus({ status }: { status: RegulatoryRegister['rows'][number]['status'] }) {
  if (status === 'Due soon') return <Pill tone="warning" className={PILL_SM}>{status}</Pill>;
  if (status === 'Overdue') return <Pill tone="danger" className={PILL_SM}>{status}</Pill>;
  return (
    <span className="whitespace-nowrap text-xs font-semibold text-ink-secondary">
      <span aria-hidden className="mr-1.5 text-muted">✓</span>
      {status}
    </span>
  );
}

function CycleBar({ elapsed, dueSoonStart }: { elapsed: number; dueSoonStart: number }) {
  return (
    <div className="relative h-2 min-w-[120px] rounded-[3px] bg-[var(--hairline)]">
      <em
        aria-hidden
        className="absolute inset-y-0 right-0 rounded-r-[3px] border-l border-dashed border-[var(--state-warning)]"
        style={{ left: `${dueSoonStart}%`, background: WARN_SOFT }}
      />
      <i className="absolute inset-y-0 left-0 rounded-[3px] bg-[var(--ink-secondary)]" style={{ width: `${elapsed}%` }} />
      <span aria-hidden className="absolute -bottom-[3px] -top-[3px] border-l-[1.5px] border-[var(--ink-primary)]" style={{ left: `${elapsed}%` }} />
    </div>
  );
}

function RegulatoryObligations({ read }: { read: RegulatoryRegister }) {
  return (
    <DowntimeCard
      title="Regulatory obligations"
      meta={
        <>
          sorted by next due · <span className="font-semibold text-ink-secondary">bar</span> = cycle elapsed · shaded = due-soon window ({read.dueSoonRule})
        </>
      }
      className={FLUSH_CARD}
      aria-label="Regulatory obligations"
    >
      <div className={FLUSH_TABLE}>
        <Table wrap={false} className="tabular-nums" data-regulatory-register>
          <THead>
            <TR>
              <TH>Requirement</TH>
              <TH>Cadence</TH>
              <TH className="text-right">Last completed</TH>
              <TH className="text-right">Next due</TH>
              <TH className="w-[15%]">Cycle</TH>
              <TH>Owner</TH>
              <TH>Authority</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {read.rows.map((row) => (
              <TR key={row.id} data-obligation={row.id}>
                <TD>
                  <span className="block text-[12.5px] font-semibold text-ink-primary">{row.requirement}</span>
                  {row.reading ? (
                    <span data-obligation-reading={row.id} className="block text-xs text-muted">
                      <span className="font-semibold text-ink-secondary">{row.reading.figure}</span>
                      {row.reading.rest}
                    </span>
                  ) : null}
                </TD>
                <TD>{row.frequency}</TD>
                <TD className="whitespace-nowrap text-right">{row.lastCompleted}</TD>
                <TD className="whitespace-nowrap text-right">
                  <span className="text-xs font-semibold text-ink-primary">{row.nextDue}</span>
                  <span className="ml-1 text-[11px] text-muted">in {row.dueInDays} d</span>
                </TD>
                <TD>
                  <CycleBar elapsed={row.cycleElapsedPct} dueSoonStart={row.dueSoonStartPct} />
                </TD>
                <TD>
                  <HumanChip name={row.owner} />
                </TD>
                <TD className="text-xs">{row.authority}</TD>
                <TD>
                  <ObligationStatus status={row.status} />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </DowntimeCard>
  );
}

// --- Crew compliance -----------------------------------------------------------

function CrewFacts({ cells }: { cells: readonly CrewKpiCell[] }) {
  return (
    <div className="mb-2.5 grid grid-cols-2 border-y border-[var(--hairline)] md:grid-cols-4">
      {cells.map((cell) => (
        <div key={cell.id} data-crew-fact={cell.id} className="flex min-w-0 flex-col gap-px border-r border-[var(--hairline)] px-3 py-2 first:pl-0 last:border-r-0">
          {/* Below 1400 two of the four labels run to two lines ("Sample
              compliance", "Reports & records"); the reserved 27 px holds all
              four figures on one baseline. Above 1400 every label is one line. */}
          <span className="min-h-[27px] text-xs font-semibold uppercase leading-tight tracking-[0.07em] text-muted min-[1400px]:min-h-0">{cell.label}</span>
          <span className="text-lg font-semibold leading-tight tabular-nums text-ink-primary">{cell.value}</span>
          <span className="text-xs leading-snug tabular-nums text-muted">
            {cell.sub}
            {cell.delta ? (
              <>
                {' · '}
                <span className="whitespace-nowrap font-semibold" style={{ color: vizToneColor(cell.delta.tone) }}>
                  {cell.delta.arrow} {cell.delta.text}
                </span>
              </>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function CrewMatrix({ table }: { table: RoleTable }) {
  return (
    <div
      className={cn(
        CARD_TABLE,
        '[&_th]:!px-1 [&_td]:!px-1 min-[1400px]:[&_th]:!px-2 min-[1400px]:[&_td]:!px-2',
        // Below 1400 the 4 px cell padding leaves the Shifts value ("4") touching
        // the Samples figure ("88.0% (22/25)"); a wider right gutter makes it
        // read as its own column without pushing the wrapper into a scroll.
        'max-[1399px]:[&_th:nth-child(2)]:!pr-3 max-[1399px]:[&_td:nth-child(2)]:!pr-3',
      )}
    >
      <Table wrap={false} className="tabular-nums" data-crew-matrix>
        <THead>
          <TR>
            {table.columns.map((column, index) => (
              <TH key={column} className={index > 0 ? 'text-right' : undefined}>{column}</TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {table.rows.map((row) => {
            const total = row.id === 'cr-t';
            return (
              <TR
                key={row.id}
                data-crew-row={row.id}
                className={total ? 'border-t border-[var(--hairline-strong)] bg-[var(--surface-1)] hover:bg-[var(--surface-1)] dark:hover:bg-[var(--surface-1)]' : undefined}
              >
                {row.cells.map((cell, index) => {
                  const tone = total ? null : crewComplianceCellTone(table.columns[index] ?? '', cell);
                  return (
                    <TD
                      key={`${row.id}-${index}`}
                      data-tone={tone ?? 'none'}
                      className={cn(
                        total && index === 0 ? 'whitespace-normal' : 'whitespace-nowrap',
                        index === 0 ? 'font-medium !text-ink-primary' : 'text-right',
                        total ? 'font-semibold !text-ink-primary' : undefined,
                        tone ? 'font-semibold' : undefined,
                      )}
                      style={tone ? { color: vizToneColor(tone) } : undefined}
                    >
                      {cell}
                    </TD>
                  );
                })}
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}

/** The last seven complete days against the target, axis from 70 %. */
function CrewTrendChart({ trend }: { trend: CrewTrend }) {
  return (
    <div data-crew-trend>
      <div className="relative mt-[18px] grid h-[150px] grid-cols-7 items-end gap-1.5 border-b border-[var(--hairline-strong)]">
        <span
          aria-hidden
          className="absolute inset-x-0 border-t-[1.5px] border-dashed border-[var(--ink-tertiary)]"
          style={{ bottom: `${trend.targetBottom}%` }}
        />
        {trend.bars.map((bar) => (
          <div key={bar.id} data-crew-trend-bar={bar.day} className="flex h-full flex-col items-center justify-end">
            <span className="mb-0.5 text-[10px] font-semibold tabular-nums text-ink-secondary">{bar.value.toFixed(1)}</span>
            <i className="block w-[70%] rounded-t-[3px] bg-[var(--ink-secondary)]" style={{ height: `${bar.height}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-[3px] grid grid-cols-7 gap-1.5 text-center text-[10px] text-muted">
        {trend.bars.map((bar) => <span key={bar.id} data-crew-trend-label={bar.day} className="whitespace-nowrap">{bar.day}</span>)}
      </div>
    </div>
  );
}

function CrewGaps({ gaps }: { gaps: readonly CrewGap[] }) {
  return (
    <div>
      {gaps.map((gap) => (
        <div key={gap.id} data-crew-gap={gap.id} className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-baseline gap-2.5 border-b border-[var(--hairline)] py-[7px] text-[12.5px] last:border-b-0">
          <span className="flex items-center gap-1 font-semibold tabular-nums text-[var(--state-blocked)]">
            <TriangleAlert className="h-[11px] w-[11px] shrink-0" aria-hidden />
            {gap.count}
          </span>
          <span className="text-ink-primary">{gap.text}</span>
          <span className="whitespace-nowrap text-xs text-muted">{gap.crew}</span>
        </div>
      ))}
    </div>
  );
}

function CrewComplianceTab() {
  const tables = tablesForComplianceTab('crew').map(crewTableWithRollingLabel);
  const crewTable = tables.find((table) => table.caption.startsWith('Crew compliance by category'));
  const caption = crewTable ? crewMatrixCaption(crewTable.caption) : null;
  const trend = crewTrend();

  return (
    <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
      {crewTable && caption ? (
        <DowntimeCard title={caption.title} meta={caption.meta} className="xl:col-span-8" aria-label="Crew compliance by category">
          <CrewFacts cells={crewKpiCells()} />
          <CrewMatrix table={crewTable} />
        </DowntimeCard>
      ) : null}
      <div className="flex min-w-0 flex-col gap-3 xl:col-span-4">
        <DowntimeCard title="7-day overall compliance" meta={trend.meta} aria-label="7-day overall compliance">
          <CrewTrendChart trend={trend} />
        </DowntimeCard>
        <DowntimeCard title="Top compliance gaps" meta="this week · crew" aria-label="Top compliance gaps">
          <CrewGaps gaps={crewGaps()} />
        </DowntimeCard>
      </div>
    </div>
  );
}

// --- Safety trend --------------------------------------------------------------

const CHART_HEIGHT = 200;

/** Near misses and hazard reports by month as paired bars, the rolling-12
 *  window bracketed over them and the operating year's start dashed. */
function SafetyRecordChart({ record }: { record: SafetyRecord }) {
  const scale = (value: number) => (value / record.max) * CHART_HEIGHT;
  return (
    <div data-safety-record>
      <div className="grid grid-cols-[118px_minmax(0,1fr)] gap-x-2.5 text-xs">
        <div>
          <div className="h-6" />
          <div className="text-xs text-ink-secondary" style={{ height: CHART_HEIGHT, paddingTop: CHART_HEIGHT / 2 - 20 }}>
            Near misses
            <br />
            Hazard reports
            <span className="block text-[11px] text-muted">per month</span>
          </div>
        </div>
        <div className="relative pt-6">
          <div className="relative grid gap-x-1" style={{ gridTemplateColumns: `repeat(${record.months.length}, minmax(0, 1fr))` }}>
            <div
              data-safety-window
              className="absolute -top-1.5 right-0 h-1.5 rounded-t-[3px] border border-b-0 border-[var(--hairline-strong)]"
              style={{ left: `${record.window.left}%` }}
            >
              <span className="absolute -top-[15px] left-1/2 -translate-x-1/2 whitespace-nowrap bg-[var(--surface-2)] px-1 text-[10px] text-muted">
                {record.window.label}
              </span>
            </div>
            <div
              aria-hidden
              className="absolute -top-0.5 bottom-[30px] border-l border-dashed border-[var(--ink-tertiary)]"
              style={{ left: `calc(${record.yearOne.left}% - 2px)` }}
            >
              <span className="absolute -top-0.5 left-1 whitespace-nowrap text-[10px] font-semibold text-ink-secondary">{record.yearOne.label}</span>
            </div>
            {record.months.map((month) => (
              <div key={month.id} data-safety-month={month.id} className="flex flex-col items-center">
                <div className="flex w-full items-end justify-center gap-0.5 border-b border-[var(--hairline-strong)]" style={{ height: CHART_HEIGHT }}>
                  {[
                    { key: 'nm', value: month.nearMisses, fill: 'var(--ink-secondary)' },
                    { key: 'hz', value: month.hazardReports, fill: SOFT_BAR },
                  ].map((bar) => (
                    <i
                      key={bar.key}
                      className="relative block w-[11px] flex-none rounded-t-[2px] not-italic"
                      style={{ height: scale(bar.value), background: bar.fill, opacity: month.partial ? 0.5 : undefined }}
                    >
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-[10px] tabular-nums text-muted">{bar.value}</span>
                    </i>
                  ))}
                </div>
                <div className="mt-[3px] text-center text-[10px] leading-tight text-muted">
                  {/* The comp draws every month tick on one line at 1470; below
                      1400 the 16 columns are 25 px wide, so "1–8 Jun" must be
                      free to wrap rather than collide with May. */}
                  <span data-safety-month-label className="min-[1400px]:whitespace-nowrap">{month.label}</span>
                  <span className="block font-semibold text-ink-secondary">{month.year ?? ' '}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-3.5 text-xs text-muted">
        <span className="inline-flex items-center"><i className="mr-1.5 inline-block h-2.5 w-2.5 rounded-[3px] bg-[var(--ink-secondary)]" />Near misses</span>
        <span className="inline-flex items-center"><i className="mr-1.5 inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: SOFT_BAR }} />Hazard reports</span>
        {record.partialNote ? <span>{record.partialNote}</span> : null}
      </div>
    </div>
  );
}

function RollingFacts({ facts }: { facts: readonly RollingFact[] }) {
  return (
    <div>
      {facts.map((fact) => (
        <div key={fact.id} data-rolling-fact={fact.id} className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-2.5 border-b border-[var(--hairline)] py-[7px] text-[12.5px] last:border-b-0">
          <span className="whitespace-nowrap text-ink-secondary">{fact.label}</span>
          <span className="min-w-0 text-right font-semibold tabular-nums text-ink-primary">
            {fact.value}
            {fact.sub ? <span className="ml-1 text-xs font-normal text-muted">{fact.sub}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function SafetyTrendTab({ state }: { state: SessionState }) {
  const record = safetyRecord(state);
  return (
    <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-stretch">
      <DowntimeCard title="Monthly safety record" meta={`${record.rangeLabel} · counts per month`} className="xl:col-span-8" aria-label="Monthly safety record">
        <SafetyRecordChart record={record} />
      </DowntimeCard>
      <DowntimeCard title="Rolling 12 months" className="xl:col-span-4" aria-label="Rolling 12 months">
        <RollingFacts facts={rollingTwelveFacts(state)} />
      </DowntimeCard>
    </div>
  );
}
